import { SendOutlined } from '@ant-design/icons';
import { Button, Input, Row, Select } from 'antd';
import { SelectValue } from 'antd/lib/select';
import Modal from 'antd/lib/modal/Modal';
import { DateTime } from 'luxon';
import React, { ChangeEventHandler } from 'react';

import OnboardingAddress from '@portal/components/OnboardingAddress/OnboardingAddress';
import useReduxState from '@portal/hooks/useReduxState';
import { DocumentType } from '@portal/models/enumerators/onboarding';
import { translate } from '@portal/services/i18n';
import { removeAccent } from '@portal/services/masks';

interface PendingDocumentsModalGeneralProps {
  visible: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}

interface TextInputProps extends PendingDocumentsModalGeneralProps {
  isSelect?: false | undefined;
  isAddress?: false | undefined;
  documentType:
    | DocumentType.CPF
    | DocumentType.BORN_AT
    | DocumentType.ESTABLISH_FORM
    | DocumentType.FINANCIAL_STATISTIC
    | DocumentType.MOTHER_NAME;
  value: string;
  options?: undefined;
  onChange: ChangeEventHandler<HTMLInputElement>;
  addressData?: undefined;
}

interface SelectInputProps extends PendingDocumentsModalGeneralProps {
  isSelect: true;
  isAddress?: false | undefined;
  documentType: DocumentType.BUSINESS_LINE;
  value: string;
  options: models.AdvancedEnum[];
  onChange: (value: number) => void;
  addressData?: undefined;
}

interface AddressInputProps extends PendingDocumentsModalGeneralProps {
  isSelect?: false | undefined;
  isAddress: true;
  documentType: DocumentType.ADDRESS;
  value?: undefined;
  options?: undefined;
  onChange: (key: string, value: string) => void;
  addressData: models.Address;
}

const PendingDocumentsModal = ({
  isSelect,
  isAddress,
  addressData,
  visible,
  value,
  options,
  documentType,
  onSubmit,
  onCancel,
  onChange,
}: AddressInputProps | TextInputProps | SelectInputProps) => {
  const { loadUploadImages } = useReduxState().loading;

  const isDisabled = React.useMemo(() => {
    if (documentType === DocumentType.BORN_AT) {
      const date = DateTime.fromFormat(
        value ?? '',
        translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON')
      );
      const isValid = date.isValid;
      const isCoherent = DateTime.now() >= date;

      return !isValid || !isCoherent;
    }

    return false;
  }, [documentType, value]);

  const renderInput = () => (
    <div className="pending-document-modal-text__input">
      {isSelect ? (
        <Select
          allowClear
          className="pending-document-modal-text__input__select"
          showSearch
          onChange={onChange as (value: SelectValue) => void}
          filterOption={(input, option) =>
            removeAccent(option?.children ? option.children.toString() : '')
              .toLowerCase()
              .indexOf(removeAccent(input).toLowerCase()) >= 0
          }
          placeholder={translate(
            'PAGES.PENDING_DOCUMENTS.MODAL.SELECT_PLACEHOLDER'
          )}
        >
          {options?.map((option) => (
            <Select.Option key={option.id} value={option.id}>
              {option.type}
            </Select.Option>
          ))}
        </Select>
      ) : (
        <Input
          onChange={onChange as ChangeEventHandler<HTMLInputElement>}
          value={value}
          allowClear
          autoFocus
          onPressEnter={onSubmit}
        />
      )}
    </div>
  );

  const renderAddress = () => (
    <OnboardingAddress
      addressData={addressData}
      onFormChange={onChange as (key: string, value: string) => void}
    />
  );

  return (
    <Modal
      open={visible}
      footer={null}
      closable={true}
      onCancel={onCancel}
      className="pending-document-modal-text__message-modal"
    >
      <p className="pending-document-modal-text__message-modal__title">
        {translate(
          `PAGES.PENDING_DOCUMENTS.DOCUMENTS.${DocumentType[documentType]}`
        )}
      </p>
      {!isAddress ? renderInput() : renderAddress()}
      <Row
        className="pending-document-modal-text__message-modal__button"
        justify="center"
      >
        <Button
          icon={<SendOutlined />}
          type="primary"
          disabled={isDisabled}
          onClick={onSubmit}
          loading={loadUploadImages}
        >
          {translate('PAGES.PENDING_DOCUMENTS.MODAL.SUBMIT')}
        </Button>
      </Row>
    </Modal>
  );
};

export default PendingDocumentsModal;
